/**
 * AirtableSyncModal
 *
 * Native React port of the Next.js Airtable sync popup
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAirtable } from 'data/repositories/airtableRepository';
import { closeModal } from 'reducers/modalReducer';
import StandardModal from 'modals/StandardModal';
import './AirtableSyncModal.css';

const SYNC_POLL_INTERVAL_MS = 3000;

/**
 * What to tell the user for each failure the workspaces endpoint reports.
 *
 * The API sends a stable code (App\Enums\AirtableFailureCode) plus Airtable's
 * own sentence. The sentence is fine for a log and poor for a modal - "API
 * billing plan limit exceeded (HTTP 429)" does not tell anyone their Airtable
 * plan ran out of API calls this month - so the copy lives here and the code
 * selects it. `airtable_upstream_error` is deliberately absent: there is
 * nothing better to say than whatever Airtable said, so it falls through to
 * the error's own message.
 */
const WORKSPACE_ERROR_COPY = {
	airtable_quota_exceeded:
		"Your Airtable account's monthly API quota has been used up. It resets when your billing period rolls over, or you can upgrade your Airtable plan.",
	airtable_rate_limited:
		'Airtable is rate-limiting this account right now. Wait a minute and try again.',
	airtable_unauthorized:
		'Your Airtable connection has expired. Disconnect and reconnect Airtable.',
	airtable_forbidden:
		'Your Airtable connection is missing the permissions needed to list bases. Reconnect and grant schema access.',
	airtable_timeout: 'Airtable did not respond in time. Try again in a moment.',
};

// Sentinel value for the "create a brand-new base" choice in the Base dropdown.
const NEW_BASE_OPTION = '__new__';

// Mirrors App\Enums\AirtableSyncStatus on the API. `DATASETS_SYNCING` means
// Properties and Portfolios are already in Airtable and the ten data tables are
// still loading in a background job chain — terminal from the user's point of
// view, even though the sync is not finished.
const SYNC_STATUS = {
	IDLE: 'idle',
	RUNNING: 'running',
	DATASETS_SYNCING: 'datasets_syncing',
	COMPLETE: 'complete',
	DATASETS_FAILED: 'datasets_failed',
	FAILED: 'failed',
};

function getPropertyKey(propertyId) {
	return String(propertyId);
}

function uniquePropertyIds(propertyIds) {
	const seen = new Set();
	const result = [];

	(propertyIds || []).forEach((propertyId) => {
		if (propertyId === null || propertyId === undefined) {
			return;
		}

		const key = getPropertyKey(propertyId);
		if (seen.has(key)) {
			return;
		}

		seen.add(key);
		result.push(propertyId);
	});

	return result;
}

function getPortfolioKey(property) {
	if (property.portfolio_id !== null && property.portfolio_id !== undefined) {
		return `portfolio-${property.portfolio_id}`;
	}

	return `portfolio-${property.portfolio_name ?? 'other'}`;
}

function getPortfolioName(property) {
	return property.portfolio_name ?? 'Other properties';
}

function compactId(value) {
	if (!value) {
		return '';
	}

	return value.length > 16
		? `${value.slice(0, 8)}...${value.slice(-5)}`
		: value;
}

function formatLastSynced(value) {
	if (!value) {
		return null;
	}

	const normalized = value.includes('T') ? value : value.replace(' ', 'T');
	const parsed = new Date(normalized);

	if (Number.isNaN(parsed.getTime())) {
		return value;
	}

	return new Intl.DateTimeFormat('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	}).format(parsed);
}

function filterPropertyIdsByAvailable(propertyIds, availablePropertyIds) {
	if (availablePropertyIds.length === 0) {
		return uniquePropertyIds(propertyIds);
	}

	const availableKeys = new Set(availablePropertyIds.map(getPropertyKey));

	return uniquePropertyIds(
		propertyIds.filter((propertyId) =>
			availableKeys.has(getPropertyKey(propertyId)),
		),
	);
}

function AirtableSyncModal({ options }) {
	const { actions } = useAirtable();
	const dispatch = useDispatch();
	const favouriteProperty = options?.favouriteProperty;

	const [workspaceId, setWorkspaceId] = useState('');
	// Which base to sync into: NEW_BASE_OPTION = create a new base, otherwise an
	// existing base id within the selected workspace.
	const [selectedBaseId, setSelectedBaseId] = useState(NEW_BASE_OPTION);
	const [isBaseDropdownOpen, setIsBaseDropdownOpen] = useState(false);
	const [selectedPropertyIds, setSelectedPropertyIds] = useState(() =>
		uniquePropertyIds(favouriteProperty ?? []),
	);
	const [settings, setSettings] = useState(null);
	const [properties, setProperties] = useState([]);
	const [loadError, setLoadError] = useState(null);
	const [collapsedPortfolioIds, setCollapsedPortfolioIds] = useState(
		() => new Set(),
	);
	const [isLoading, setIsLoading] = useState(false);
	const [syncError, setSyncError] = useState(null);
	const [syncResult, setSyncResult] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [useSavedAirtableBase, setUseSavedAirtableBase] = useState(true);

	// What the *previous* sync left behind, read once on mount from the status
	// endpoint. Distinct from `syncResult`, which describes a sync started in
	// this modal session.
	const [backgroundSync, setBackgroundSync] = useState(null);
	const [isNoticeDismissed, setIsNoticeDismissed] = useState(false);

	const [syncFrequency, setSyncFrequency] = useState('off');
	const [isSavingSchedule, setIsSavingSchedule] = useState(false);
	const [scheduleSaved, setScheduleSaved] = useState(false);

	// OAuth state
	const [isAirtableConnected, setIsAirtableConnected] = useState(false);
	const [isCheckingOAuth, setIsCheckingOAuth] = useState(true);

	// Airtable workspaces state
	const [airtableWorkspaces, setAirtableWorkspaces] = useState([]);
	const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(false);
	// Why the workspace lookup failed, if it did. Kept separate from an account
	// that genuinely has no bases: only the latter should be told to create one.
	const [workspacesError, setWorkspacesError] = useState(null);
	const [isWorkspaceDropdownOpen, setIsWorkspaceDropdownOpen] = useState(false);

	const pollRef = useRef(null);

	const stopPolling = useCallback(() => {
		if (pollRef.current) {
			clearInterval(pollRef.current);
			pollRef.current = null;
		}
	}, []);

	const selectedKeys = useMemo(
		() => new Set(selectedPropertyIds.map(getPropertyKey)),
		[selectedPropertyIds],
	);
	const availablePropertyIds = useMemo(
		() => uniquePropertyIds(properties.map((property) => property.id)),
		[properties],
	);
	const propertyGroups = useMemo(() => {
		const groups = new Map();

		properties.forEach((property) => {
			const id = getPortfolioKey(property);
			const group = groups.get(id);

			if (group) {
				group.properties.push(property);
			} else {
				groups.set(id, {
					id,
					name: getPortfolioName(property),
					properties: [property],
				});
			}
		});

		return Array.from(groups.values());
	}, [properties]);

	const selectedWorkspaceBases = useMemo(() => {
		if (!workspaceId) {
			return [];
		}

		return (
			airtableWorkspaces.find((ws) => ws.workspaceId === workspaceId)?.bases ??
			[]
		);
	}, [airtableWorkspaces, workspaceId]);
	const selectedBaseName = useMemo(() => {
		if (selectedBaseId === NEW_BASE_OPTION) {
			return null;
		}

		return (
			selectedWorkspaceBases.find((base) => base.id === selectedBaseId)?.name ??
			null
		);
	}, [selectedWorkspaceBases, selectedBaseId]);
	const isCreatingNewBase = selectedBaseId === NEW_BASE_OPTION;

	const isReturningUser = Boolean(settings?.workspace_id && settings.base_id);
	const isUsingSavedBase = isReturningUser && useSavedAirtableBase;
	const canShowSyncControls = isAirtableConnected && !isCheckingOAuth;

	// Only the core phase blocks. The backend refuses a new sync while the status
	// is `running` (AirtableSyncStatus::blocksNewSync()), so the gate and the
	// re-entry poll cover that window and nothing else. During
	// `datasets_syncing` the properties are already in Airtable and a new sync
	// simply supersedes the running chain, so the modal stays usable.
	const isCoreSyncRunning = backgroundSync?.status === SYNC_STATUS.RUNNING;
	const savedWorkspace = useMemo(() => {
		if (!settings?.workspace_id) {
			return null;
		}

		return (
			airtableWorkspaces.find(
				(workspace) => workspace.workspaceId === settings.workspace_id,
			) ?? null
		);
	}, [airtableWorkspaces, settings?.workspace_id]);
	const savedBase = useMemo(() => {
		if (!settings?.base_id) {
			return null;
		}

		const exactWorkspaceBase = savedWorkspace?.bases.find(
			(base) => base.id === settings.base_id,
		);
		if (exactWorkspaceBase) {
			return exactWorkspaceBase;
		}

		let found = null;
		airtableWorkspaces.forEach((workspace) => {
			if (found) {
				return;
			}
			const base = workspace.bases.find(
				(entry) => entry.id === settings.base_id,
			);
			if (base) {
				found = base;
			}
		});

		return found;
	}, [airtableWorkspaces, savedWorkspace, settings?.base_id]);
	const savedBaseLabel = savedBase?.name || compactId(settings?.base_id);
	const formattedLastSynced = formatLastSynced(settings?.last_synced_at);

	// Copy for a previous sync that ended badly, or one whose background chain
	// is still running. Neither state blocks a new sync; the notice just
	// explains what happened / is happening so the user is not surprised that
	// starting a new sync restarts the still-running data set chain.
	const previousSyncNotice = useMemo(() => {
		if (isNoticeDismissed || syncResult || isSubmitting) {
			return null;
		}

		if (backgroundSync?.status === SYNC_STATUS.DATASETS_SYNCING) {
			return 'Data sets from your last sync are still loading. Syncing again will restart them.';
		}

		if (backgroundSync?.status === SYNC_STATUS.DATASETS_FAILED) {
			const failedTables = backgroundSync.failedTables ?? [];

			return failedTables.length > 0
				? `Your properties and portfolios are in Airtable, but these data sets did not finish loading: ${failedTables.join(
						', ',
					)}. You can sync again.`
				: 'Your properties and portfolios are in Airtable, but some data sets did not finish loading. You can sync again.';
		}

		if (backgroundSync?.status === SYNC_STATUS.FAILED) {
			return 'Your last sync did not finish. You can try again.';
		}

		return null;
	}, [backgroundSync, isNoticeDismissed, syncResult, isSubmitting]);
	const selectedCount =
		properties.length > 0
			? properties.filter((property) =>
					selectedKeys.has(getPropertyKey(property.id)),
				).length
			: selectedPropertyIds.length;
	const availableCount = properties.length || selectedCount;
	const allPropertiesSelected =
		availablePropertyIds.length > 0 &&
		selectedCount === availablePropertyIds.length;
	const canSync =
		!isSubmitting &&
		!isCoreSyncRunning &&
		isAirtableConnected &&
		(isUsingSavedBase || Boolean(workspaceId.trim())) &&
		selectedCount > 0;

	// Check OAuth connection status
	const checkOAuthStatus = useCallback(async () => {
		setIsCheckingOAuth(true);
		try {
			const data = await actions.getOAuthStatus();
			setIsAirtableConnected(Boolean(data?.connected));
		} catch {
			setIsAirtableConnected(false);
		} finally {
			setIsCheckingOAuth(false);
		}
	}, [actions]);

	useEffect(() => {
		checkOAuthStatus();
	}, [checkOAuthStatus]);

	// Clear any active poll on unmount
	useEffect(() => stopPolling, [stopPolling]);

	// Keep the base choice valid: when the workspace changes (so its bases
	// change), fall back to "create new base" unless the selection still exists.
	useEffect(() => {
		if (
			selectedBaseId !== NEW_BASE_OPTION &&
			!selectedWorkspaceBases.some((base) => base.id === selectedBaseId)
		) {
			setSelectedBaseId(NEW_BASE_OPTION);
		}
	}, [selectedWorkspaceBases, selectedBaseId]);

	// Lock background page scroll while the modal is open so the dashboard
	// behind it does not scroll (the shared modal system doesn't do this).
	useEffect(() => {
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, []);

	// Fetch workspaces and saved settings from Airtable when connected
	useEffect(() => {
		if (!isAirtableConnected) {
			setAirtableWorkspaces([]);
			return undefined;
		}

		let isMounted = true;

		async function loadWorkspaces() {
			setIsLoadingWorkspaces(true);
			try {
				const data = await actions.getWorkspaces();
				if (isMounted && data?.workspaces) {
					setAirtableWorkspaces(data.workspaces);
					// Auto-select the first workspace if none is set
					if (!workspaceId && data.workspaces.length > 0) {
						setWorkspaceId(data.workspaces[0].workspaceId);
					}
				}
			} catch (error) {
				// Surface Airtable's reason. Falling through to the empty state here
				// told users with four bases to "create a base first", most often
				// when the account's monthly API quota was exhausted.
				if (isMounted) {
					setWorkspacesError(
						WORKSPACE_ERROR_COPY[error?.code] ||
							(error instanceof Error
								? error.message
								: 'Could not load your Airtable workspaces.'),
					);
				}
			} finally {
				if (isMounted) {
					setIsLoadingWorkspaces(false);
				}
			}
		}

		loadWorkspaces();

		return () => {
			isMounted = false;
		};
		// Intentionally runs only when the connection flips. `workspaceId` is read
		// for the one-time auto-select; adding it would refetch on every change.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAirtableConnected]);

	// Load the saved sync settings (returning-user base) + syncable properties
	useEffect(() => {
		let isMounted = true;

		async function loadData() {
			setIsLoading(true);
			setLoadError(null);

			const [statusResult, propertiesResult] = await Promise.allSettled([
				actions.getSyncStatus(),
				actions.getSyncableProperties(),
			]);

			if (!isMounted) {
				return;
			}

			const status =
				statusResult.status === 'fulfilled' ? statusResult.value : null;
			const loadedSettings =
				status?.workspace_id || status?.base_id
					? {
							workspace_id: status.workspace_id ?? null,
							base_id: status.base_id ?? null,
							synced_property_ids: status.synced_property_ids ?? [],
							last_synced_at: status.last_synced_at ?? null,
						}
					: null;
			const loadedProperties =
				propertiesResult.status === 'fulfilled'
					? (propertiesResult.value?.data?.properties ?? [])
					: [];

			setSettings(loadedSettings);
			setBackgroundSync({
				status: status?.status ?? SYNC_STATUS.IDLE,
				failedTables: status?.failed_tables ?? [],
			});
			setSyncFrequency(status?.frequency ?? 'off');
			setUseSavedAirtableBase(
				Boolean(loadedSettings?.workspace_id && loadedSettings.base_id),
			);
			setProperties(loadedProperties);
			setLoadError(
				propertiesResult.status === 'rejected'
					? propertiesResult.reason instanceof Error
						? propertiesResult.reason.message
						: 'Failed to load properties.'
					: null,
			);

			if (loadedSettings?.workspace_id) {
				setWorkspaceId(loadedSettings.workspace_id);
			}

			const loadedPropertyIds = uniquePropertyIds(
				loadedProperties.map((property) => property.id),
			);

			setSelectedPropertyIds((current) => {
				const currentVisibleIds = filterPropertyIdsByAvailable(
					current,
					loadedPropertyIds,
				);
				if (currentVisibleIds.length > 0) {
					return currentVisibleIds;
				}

				if (
					loadedSettings?.synced_property_ids &&
					loadedSettings.synced_property_ids.length > 0
				) {
					return filterPropertyIdsByAvailable(
						loadedSettings.synced_property_ids,
						loadedPropertyIds,
					);
				}

				return loadedPropertyIds;
			});

			setIsLoading(false);
		}

		loadData();

		return () => {
			isMounted = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// While the core phase is still running, keep checking so the modal
	// unblocks itself the moment it advances - no reload needed. Advancing to
	// `datasets_syncing` counts as unblocked: the properties have landed, so
	// this poll must update state for that status too, not just terminal ones.
	useEffect(() => {
		if (!isCoreSyncRunning) {
			return undefined;
		}

		let isMounted = true;

		const interval = setInterval(async () => {
			let status;
			try {
				status = await actions.getSyncStatus();
			} catch {
				return;
			}

			if (!isMounted || !status || status.status === SYNC_STATUS.RUNNING) {
				return;
			}

			setBackgroundSync({
				status: status.status,
				failedTables: status.failed_tables ?? [],
			});
		}, SYNC_POLL_INTERVAL_MS);

		return () => {
			isMounted = false;
			clearInterval(interval);
		};
		// `actions` is useMemo'd on `[dispatch]` in createRepository.js and
		// `dispatch` is referentially stable, so including it here is harmless -
		// it never causes this interval to restart.
	}, [actions, isCoreSyncRunning]);

	function toggleProperty(propertyId) {
		const key = getPropertyKey(propertyId);

		setSelectedPropertyIds((current) => {
			if (current.some((currentId) => getPropertyKey(currentId) === key)) {
				return current.filter((currentId) => getPropertyKey(currentId) !== key);
			}

			return uniquePropertyIds([...current, propertyId]);
		});
	}

	function toggleAllProperties() {
		if (availablePropertyIds.length === 0) {
			return;
		}

		setSelectedPropertyIds(availablePropertyIds);
	}

	function togglePortfolioGroup(portfolioId) {
		setCollapsedPortfolioIds((current) => {
			const next = new Set(current);

			if (next.has(portfolioId)) {
				next.delete(portfolioId);
			} else {
				next.add(portfolioId);
			}

			return next;
		});
	}

	async function connectAirtable() {
		try {
			const data = await actions.startOAuth();
			const authorizeUrl = data?.authorize_url;
			if (!authorizeUrl) {
				setSyncError('Could not start Airtable authorization.');
				return;
			}

			const popup = window.open(authorizeUrl, '_blank', 'width=600,height=700');

			// Poll for the popup to close (user completed or cancelled OAuth)
			if (popup) {
				const pollInterval = setInterval(() => {
					if (popup.closed) {
						clearInterval(pollInterval);
						checkOAuthStatus();
					}
				}, 500);
			}
		} catch (error) {
			setSyncError(
				error instanceof Error
					? error.message
					: 'Could not start Airtable authorization.',
			);
		}
	}

	async function disconnectAirtable() {
		try {
			await actions.disconnect();
			setIsAirtableConnected(false);
		} catch {
			// Ignore disconnect errors
		}
	}

	function selectNewAirtableBase() {
		setUseSavedAirtableBase(false);
		setSyncError(null);
		setIsWorkspaceDropdownOpen(false);

		if (
			airtableWorkspaces.length > 0 &&
			!airtableWorkspaces.some(
				(workspace) => workspace.workspaceId === workspaceId,
			)
		) {
			setWorkspaceId(airtableWorkspaces[0].workspaceId);
		}
	}

	function selectSavedAirtableBase() {
		setUseSavedAirtableBase(true);
		setWorkspaceId(settings?.workspace_id ?? '');
		setSyncError(null);
		setIsWorkspaceDropdownOpen(false);
	}

	async function runSync() {
		setIsSubmitting(true);
		setSyncError(null);
		setSyncResult(null);
		stopPolling();

		const syncPropertyIds = filterPropertyIdsByAvailable(
			selectedPropertyIds,
			availablePropertyIds,
		);
		const effectiveWorkspaceId = isUsingSavedBase
			? settings?.workspace_id
			: workspaceId;
		const effectiveBaseId = isUsingSavedBase
			? settings?.base_id
			: isCreatingNewBase
				? null
				: selectedBaseId;

		try {
			await actions.runSync({
				payload: {
					propertyIds: syncPropertyIds,
					workspaceId: effectiveWorkspaceId,
					baseId: effectiveBaseId,
				},
			});
		} catch (error) {
			const message = error instanceof Error ? error.message : 'Sync failed.';
			if (/not connected|authorize via OAuth/i.test(message)) {
				setIsAirtableConnected(false);
			}
			setSyncError(message);
			setIsSubmitting(false);
			return;
		}

		// The POST returns immediately; poll the status endpoint until terminal.
		pollRef.current = setInterval(async () => {
			let status;
			try {
				status = await actions.getSyncStatus();
			} catch {
				return;
			}

			if (!status) {
				return;
			}

			// `datasets_syncing` releases the user: the properties are in Airtable
			// and the dataset chain keeps running after the modal closes.
			if (
				status.status === SYNC_STATUS.DATASETS_SYNCING ||
				status.status === SYNC_STATUS.COMPLETE
			) {
				stopPolling();
				setSyncResult({
					phase: status.status,
					message: status.message || 'Data synced successfully.',
					// The server's `synced_property_ids` is the AUTHORIZED set (see
					// RunAirtableSyncJob::authorizedPropertyIds() on the backend) and
					// can be smaller than what the client asked to sync, so prefer it
					// when present and fall back to the client's count otherwise.
					propertyCount: Array.isArray(status.synced_property_ids)
						? status.synced_property_ids.length
						: syncPropertyIds.length,
					baseId: status.base_id ?? effectiveBaseId ?? null,
				});
				setIsSubmitting(false);

				if (!isUsingSavedBase && status.base_id && effectiveWorkspaceId) {
					setSettings({
						workspace_id: effectiveWorkspaceId,
						base_id: status.base_id,
						synced_property_ids: syncPropertyIds,
						last_synced_at: status.last_synced_at ?? null,
					});
					setUseSavedAirtableBase(true);
				}
			} else if (
				status.status === SYNC_STATUS.FAILED ||
				status.status === SYNC_STATUS.DATASETS_FAILED
			) {
				stopPolling();
				setSyncError(status.error || 'Sync failed.');
				setIsSubmitting(false);
			}
		}, SYNC_POLL_INTERVAL_MS);
	}

	async function saveSchedule() {
		setIsSavingSchedule(true);
		setScheduleSaved(false);

		const syncPropertyIds = filterPropertyIdsByAvailable(
			selectedPropertyIds,
			availablePropertyIds,
		);
		const payload = { frequency: syncFrequency };
		// Only send propertyIds when there is something to send. If properties
		// have not loaded yet, failed to load, or the user deselected
		// everything, sending an empty array would overwrite a working saved
		// set with nothing - the schedule would show "Saved" while the
		// scheduled runner silently skips this user forever.
		if (syncPropertyIds.length > 0) {
			payload.propertyIds = syncPropertyIds;
		}

		try {
			const response = await actions.saveSchedule({ payload });
			setScheduleSaved(true);
			// The server's synced_property_ids is the AUTHORIZED set (property
			// ids outside the user's visibility are dropped) and can differ from
			// what the client asked to save, so reflect what was actually kept.
			if (Array.isArray(response?.synced_property_ids)) {
				setSelectedPropertyIds(
					filterPropertyIdsByAvailable(
						response.synced_property_ids,
						availablePropertyIds,
					),
				);
			}
		} catch (error) {
			setSyncError(
				error instanceof Error ? error.message : 'Could not save the schedule.',
			);
		} finally {
			setIsSavingSchedule(false);
		}
	}

	function handleClose() {
		stopPolling();
		dispatch(closeModal());
	}

	return (
		<StandardModal
			className=""
			title="Sync properties"
			size="lg"
			noFooter
			onClose={stopPolling}
		>
			<div className="airtable-sync-modal">
				<div className="popup-header">
					<div className="popup-title-row">
						<div>
							<p className="popup-subtitle">
								{!canShowSyncControls
									? 'Connect Airtable to continue.'
									: isUsingSavedBase
										? `Sync to ${savedBaseLabel || 'the saved Airtable base'}.`
										: isReturningUser
											? 'Create a new Airtable base.'
											: 'Select properties and connect them to Airtable.'}
							</p>
						</div>
						{isAirtableConnected && !syncResult && !isSubmitting ? (
							<div className="airtable-header-action">
								{isCheckingOAuth ? (
									<button
										className="airtable-status-button checking"
										disabled
										type="button"
									>
										<span className="spinner dark" aria-hidden="true" />
										Checking
									</button>
								) : (
									<button
										aria-label="Disconnect Airtable"
										className="airtable-status-button disconnect"
										onClick={() => {
											disconnectAirtable();
										}}
										title="Disconnect Airtable"
										type="button"
									>
										<span
											aria-hidden="true"
											className="airtable-connected-state"
										>
											<span className="airtable-status-dot" />
											Connected
										</span>
										<span
											aria-hidden="true"
											className="airtable-disconnect-state"
										>
											Disconnect
										</span>
									</button>
								)}
							</div>
						) : null}
					</div>
				</div>

				<div className="popup-body">
					{!canShowSyncControls && !syncResult && !isSubmitting ? (
						<div className="connection-empty" role="status">
							<strong>Airtable is not connected</strong>
							{isCheckingOAuth ? (
								<button
									className="airtable-status-button checking connection-empty-action"
									disabled
									type="button"
								>
									<span className="spinner dark" aria-hidden="true" />
									Checking connection
								</button>
							) : (
								<button
									className="airtable-status-button connect connection-empty-action"
									onClick={connectAirtable}
									type="button"
								>
									Connect
								</button>
							)}
						</div>
					) : canShowSyncControls &&
					  isReturningUser &&
					  !syncResult &&
					  !isSubmitting ? (
						<div className="sync-target">
							<div className="sync-target-head">
								<span className="summary-label">Sync target</span>
								<div
									className="sync-target-toggle"
									role="group"
									aria-label="Sync target"
								>
									<button
										aria-pressed={isUsingSavedBase}
										className={isUsingSavedBase ? 'active' : ''}
										onClick={selectSavedAirtableBase}
										type="button"
									>
										Saved base
									</button>
									<button
										aria-pressed={!isUsingSavedBase}
										className={!isUsingSavedBase ? 'active' : ''}
										onClick={selectNewAirtableBase}
										type="button"
									>
										New base
									</button>
								</div>
							</div>
							{isUsingSavedBase ? (
								<div className="sync-target-meta">
									{formattedLastSynced ? (
										<p className="sync-target-timestamp">
											Last synced{' '}
											<time dateTime={settings?.last_synced_at ?? undefined}>
												{formattedLastSynced}
											</time>
										</p>
									) : null}
								</div>
							) : null}
						</div>
					) : null}

					{previousSyncNotice ? (
						<div className="message notice" role="status">
							<span>{previousSyncNotice}</span>
							<button
								aria-label="Dismiss"
								className="message-dismiss"
								onClick={() => setIsNoticeDismissed(true)}
								type="button"
							>
								&times;
							</button>
						</div>
					) : null}

					{syncResult ? (
						<div className="sync-complete" role="status" aria-live="polite">
							<span className="sync-complete-mark" aria-hidden="true" />
							{syncResult.phase === SYNC_STATUS.DATASETS_SYNCING ? (
								<div>
									<h2>Properties synced</h2>
									<p>
										{syncResult.propertyCount}{' '}
										{syncResult.propertyCount === 1
											? 'property and its portfolio are'
											: 'properties and their portfolios are'}{' '}
										now in Airtable. Data sets (violations, permits,
										complaints) are still loading in the background - you can
										close this window.
									</p>
									{syncResult.baseId ? (
										<a
											className="sync-complete-link"
											href={`https://airtable.com/${syncResult.baseId}`}
											rel="noopener noreferrer"
											target="_blank"
										>
											Open in Airtable
										</a>
									) : null}
								</div>
							) : (
								<div>
									<h2>Sync complete</h2>
									<p>{syncResult.message ?? 'Data synced successfully.'}</p>
								</div>
							)}
						</div>
					) : isSubmitting ? (
						<div className="sync-progress" role="status" aria-live="polite">
							<span className="sync-progress-spinner" aria-hidden="true" />
							<div>
								<h2>Syncing properties to Airtable</h2>
								<p>
									This can take up to 5 minutes. You can keep this window open
									while we prepare the Airtable data.
								</p>
							</div>
						</div>
					) : isCoreSyncRunning ? (
						<div className="sync-progress" role="status" aria-live="polite">
							<span className="sync-progress-spinner" aria-hidden="true" />
							<div>
								<h2>A sync is already in progress</h2>
								<p>
									Your properties and portfolios have not reached Airtable yet.
									This can take a few minutes - you can start another sync once
									it finishes.
								</p>
							</div>
						</div>
					) : (
						<>
							{!isUsingSavedBase && canShowSyncControls ? (
								<div className="form-grid">
									<div className="field">
										<div className="field-label-row">
											<label>
												{isReturningUser
													? 'Workspace for new base'
													: 'Workspace'}
											</label>
											<span
												className="tooltip-trigger"
												data-tooltip="Select the Airtable workspace for the sync base. A successful sync saves the new base as the active Violerts Airtable base."
											>
												i
											</span>
										</div>
										{isLoadingWorkspaces ? (
											<div className="oauth-status checking">
												<span className="spinner dark" aria-hidden="true" />
												<span>Loading workspaces...</span>
											</div>
										) : airtableWorkspaces.length > 0 ? (
											<div className="custom-select-container">
												<button
													type="button"
													className={`custom-select-trigger ${
														isWorkspaceDropdownOpen ? 'open' : ''
													}`}
													onClick={() =>
														setIsWorkspaceDropdownOpen(!isWorkspaceDropdownOpen)
													}
													disabled={isLoading}
												>
													{workspaceId
														? airtableWorkspaces.find(
																(ws) => ws.workspaceId === workspaceId,
															)?.name || 'Select a workspace'
														: 'Select a workspace'}
												</button>

												{isWorkspaceDropdownOpen && (
													<>
														<div
															className="custom-select-backdrop"
															onClick={() => setIsWorkspaceDropdownOpen(false)}
														/>
														<div className="custom-select-dropdown">
															<div className="custom-select-options">
																{airtableWorkspaces.map((ws) => (
																	<button
																		key={ws.workspaceId}
																		type="button"
																		className={`custom-select-option ${
																			workspaceId === ws.workspaceId
																				? 'selected'
																				: ''
																		}`}
																		onClick={() => {
																			setWorkspaceId(ws.workspaceId);
																			setIsWorkspaceDropdownOpen(false);
																		}}
																	>
																		<strong>{ws.name}</strong>
																		<small>
																			{ws.bases.length}{' '}
																			{ws.bases.length === 1 ? 'base' : 'bases'}
																		</small>
																	</button>
																))}
															</div>
														</div>
													</>
												)}
											</div>
										) : workspacesError ? (
											<div className="workspace-empty" role="status">
												{workspacesError}
											</div>
										) : (
											<div className="workspace-empty">
												No workspaces found. Create a base in Airtable first or
												check your OAuth permissions.
											</div>
										)}
									</div>
								</div>
							) : null}

							{!isUsingSavedBase &&
							canShowSyncControls &&
							airtableWorkspaces.length > 0 &&
							workspaceId ? (
								<div className="form-grid">
									<div className="field">
										<div className="field-label-row">
											<label>Base</label>
											<span
												className="tooltip-trigger"
												data-tooltip="Choose an existing base in this workspace, or create a new one. A 'Properties' table is set up automatically if the base doesn't have one."
											>
												i
											</span>
										</div>
										<div className="custom-select-container">
											<button
												type="button"
												className={`custom-select-trigger ${
													isBaseDropdownOpen ? 'open' : ''
												}`}
												onClick={() => setIsBaseDropdownOpen(!isBaseDropdownOpen)}
												disabled={isLoading}
											>
												{isCreatingNewBase
													? '➕ Create new base'
													: selectedBaseName || 'Select a base'}
											</button>

											{isBaseDropdownOpen && (
												<>
													<div
														className="custom-select-backdrop"
														onClick={() => setIsBaseDropdownOpen(false)}
													/>
													<div className="custom-select-dropdown">
														<div className="custom-select-options">
															<button
																type="button"
																className={`custom-select-option ${
																	isCreatingNewBase ? 'selected' : ''
																}`}
																onClick={() => {
																	setSelectedBaseId(NEW_BASE_OPTION);
																	setIsBaseDropdownOpen(false);
																}}
															>
																<strong>➕ Create new base</strong>
																<small>A new Violerts base in this workspace</small>
															</button>
															{selectedWorkspaceBases.map((base) => (
																<button
																	key={base.id}
																	type="button"
																	className={`custom-select-option ${
																		selectedBaseId === base.id ? 'selected' : ''
																	}`}
																	onClick={() => {
																		setSelectedBaseId(base.id);
																		setIsBaseDropdownOpen(false);
																	}}
																>
																	<strong>{base.name}</strong>
																	<small>Existing base</small>
																</button>
															))}
														</div>
													</div>
												</>
											)}
										</div>
									</div>
								</div>
							) : null}
							{canShowSyncControls ? (
								<div className="property-section">
									<div className="property-section-head">
										<div>
											<span className="summary-label">Properties</span>
											<p className="property-count">
												{isLoading
													? 'Loading properties...'
													: `${selectedCount} of ${availableCount} selected`}
											</p>
										</div>
										{!isLoading && availablePropertyIds.length > 0 ? (
											<button
												className="button secondary compact property-toggle-button"
												disabled={allPropertiesSelected}
												onClick={toggleAllProperties}
												type="button"
											>
												{allPropertiesSelected ? 'All selected' : 'Select all'}
											</button>
										) : null}
									</div>

									{isLoading ? (
										<div
											className="property-loader"
											role="status"
											aria-live="polite"
										>
											<span className="spinner dark" aria-hidden="true" />
											<span>Loading Violerts data</span>
										</div>
									) : loadError ? (
										<div className="property-error" role="status">
											<strong>Properties could not load</strong>
											<span>{loadError}</span>
										</div>
									) : propertyGroups.length > 0 ? (
										<div className="property-list">
											{propertyGroups.map((group) => (
												<div className="property-group" key={group.id}>
													<button
														aria-expanded={!collapsedPortfolioIds.has(group.id)}
														className="property-group-toggle"
														onClick={() => togglePortfolioGroup(group.id)}
														type="button"
													>
														<span>{group.name}</span>
														<small>
															{
																group.properties.filter((property) =>
																	selectedKeys.has(getPropertyKey(property.id)),
																).length
															}{' '}
															of {group.properties.length}
														</small>
													</button>
													{!collapsedPortfolioIds.has(group.id) ? (
														<div className="property-group-list">
															{group.properties.map((property, index) => (
																<label
																	className={
																		selectedKeys.has(
																			getPropertyKey(property.id),
																		)
																			? 'property-option selected'
																			: 'property-option'
																	}
																	key={`${getPropertyKey(property.id)}-${
																		group.id
																	}-${index}`}
																>
																	<input
																		className="property-input"
																		checked={selectedKeys.has(
																			getPropertyKey(property.id),
																		)}
																		onChange={() => toggleProperty(property.id)}
																		type="checkbox"
																	/>
																	<span
																		className="property-check"
																		aria-hidden="true"
																	/>
																	<span className="property-copy">
																		<strong>
																			{property.address ??
																				`Property ${property.id}`}
																		</strong>
																		<small>ID {property.id}</small>
																	</span>
																</label>
															))}
														</div>
													) : null}
												</div>
											))}
										</div>
									) : selectedPropertyIds.length > 0 ? (
										<div className="property-pill-row">
											{selectedPropertyIds.map((propertyId) => (
												<span
													className="property-pill"
													key={getPropertyKey(propertyId)}
												>
													{propertyId}
												</span>
											))}
										</div>
									) : (
										<div className="message empty">
											No properties available to sync.
										</div>
									)}
								</div>
							) : null}

							{canShowSyncControls ? (
								<div className="schedule-row">
									<label htmlFor="airtable-sync-frequency">Automatic sync</label>
									<select
										className="schedule-select"
										disabled={isSavingSchedule}
										id="airtable-sync-frequency"
										onChange={(event) => {
											setSyncFrequency(event.target.value);
											setScheduleSaved(false);
										}}
										value={syncFrequency}
									>
										<option value="off">Off</option>
										<option value="daily">Daily</option>
										<option value="weekly">Weekly</option>
									</select>
									<button
										className="button secondary compact"
										disabled={isSavingSchedule || isLoading}
										onClick={() => {
											saveSchedule();
										}}
										type="button"
									>
										{isSavingSchedule ? 'Saving...' : 'Save schedule'}
									</button>
									{scheduleSaved ? (
										<span className="schedule-saved" role="status">
											Saved
										</span>
									) : null}
								</div>
							) : null}
						</>
					)}

					{syncError ? <div className="message error">{syncError}</div> : null}

					<div className="footer-row">
						<div className="action-row">
							<button
								className="button secondary"
								disabled={isSubmitting}
								onClick={handleClose}
								type="button"
							>
								Close
							</button>
							{canShowSyncControls && !isSubmitting && !syncResult ? (
								<button
									className="button primary"
									disabled={!canSync}
									onClick={() => {
										runSync();
									}}
									type="button"
								>
									{isUsingSavedBase || !isCreatingNewBase
										? 'Sync selected'
										: 'Create base & sync'}
								</button>
							) : null}
						</div>
					</div>
				</div>
			</div>
		</StandardModal>
	);
}

export default AirtableSyncModal;
